import React, { useEffect } from 'react';
import { Anchor, Stack, Text } from '@mantine/core';
import { CustomLoader } from 'app/views/components/loader/loader';
import MainTable from 'app/views/components/Table/MainTable';
import { columnDefs } from './trade-entry-panel-columnDefs';
import { SubTradeParams } from 'app/models/sub-trade-params';
import { useDispatch, useSelector } from 'react-redux';
import { getBusinessDate } from 'app/state/ducks/business-date/selectors';
import moment from 'moment';
import { businessDaysDiffWithHolidays, formatDateToShow } from 'app/utils/util-functions';
import { getDisplayedSettlementDate, resolveTradeHolidayDates } from './utils/master-trade-util';
import { getCalendars } from 'app/state/ducks/holiday-calendars/selectors';
import { TradeListRow } from './utils/trade-list-rows';
import { loadTradeSubTrades } from 'app/state/ducks/trade-sub-trades/thunks';
import {
  getTradeSubTrades,
  hasTradeSubTradesFailed,
  hasTradeSubTradesLoaded,
  isTradeSubTradesLoading,
} from 'app/state/ducks/trade-sub-trades/selectors';

interface DetailPanelProps {
  row: TradeListRow;
  isExpanded: boolean;
}

/**
 * The sub trades of one master trade, shown when its row is expanded.
 *
 * ONE request per expand: GET /api/trade-list/:id/sub-trades returns the
 * trade's sub trades with buyer, seller and traded facilities (facility name,
 * CUSIP, state, currency) already resolved. Nothing tenant-wide is loaded —
 * before, an expand pulled every sub trade, entity, facility, locked facility
 * and traded facility. The panel adds only what needs the browser's data: the
 * displayed settlement date and T+ (business date, calendars).
 */
const TradeEntryPanel: React.FC<DetailPanelProps> = ({ row, isExpanded }) => {
  const dispatch = useDispatch()
  const items = useSelector(getTradeSubTrades(row.id))
  const isLoading = useSelector(isTradeSubTradesLoading(row.id))
  const hasLoaded = useSelector(hasTradeSubTradesLoaded(row.id))
  const hasFailed = useSelector(hasTradeSubTradesFailed(row.id))
  const businessDate = useSelector(getBusinessDate)
  const holidayCalendars = useSelector(getCalendars)

  useEffect(() => {
    if (!isExpanded) return
    dispatch(loadTradeSubTrades(row.id) as any)
  }, [isExpanded, row.id, dispatch])

  const holidays = resolveTradeHolidayDates(row.tradeAssociation, holidayCalendars)

  // The column definitions were written for the stitched shape: the traded
  // facility with a `facilityDetails` record and a `facilityId` key.
  const refinedSubTrades = items.map(trade => {
    const momentTPlus = trade.lockedDate ? moment(trade.lockedDate) : moment(businessDate)
    const displayedSettlementDate = getDisplayedSettlementDate(trade, row, businessDate, holidayCalendars);
    return {
      ...trade,
      tradedFacilities: trade.tradedFacilities.map(tf => ({
        facilityId: { id: tf.facilityId },
        tradeAmount: tf.tradeAmount,
        price: tf.price,
        amounts: tf.amounts,
        facilityDetails: { name: tf.name, cusip: tf.cusip, status: tf.status, baseCurrency: tf.baseCurrency },
      })),
      targetSettlementDate: formatDateToShow(displayedSettlementDate),
      tPlus: businessDaysDiffWithHolidays(moment(row.tradeDate ?? undefined), momentTPlus, holidays),
    }
  }) as unknown as SubTradeParams[]

  // The panel must have height from the first frame: the row's collapse
  // measures it two frames after opening and animates to that height, and an
  // empty panel measures zero — no transition, so the collapse stays at
  // height 0 and the rows arriving later stay hidden until the next expand.
  // So the panel always renders something: a loader until the trade's read
  // has landed (from the very first frame — the request starts in an effect,
  // so the loading flag alone is false for that frame), an empty state after,
  // the table once there are rows. A failed read ends the wait: the panel
  // says so and offers a retry, rather than spinning forever.
  if (refinedSubTrades.length === 0) {
    const pending = isLoading || (!hasLoaded && !hasFailed)
    return (
      <Stack w="100%" className="sectionDetailTradeBody noHeader" align="center" style={{ padding: '10px' }}>
        {pending ? (
          // the app's spinner (the 150px ring with the logo), at half size so
          // the row grows by ~75px, not a page
          <div
            data-testid="sub-trades-loading"
            style={{ height: 75, width: 75, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          >
            <div style={{ transform: 'scale(0.5)', transformOrigin: 'center' }}>
              <CustomLoader />
            </div>
          </div>
        ) : hasFailed ? (
          <Text data-testid="sub-trades-failed" style={{ fontFamily: 'Plus Jakarta Sans', fontSize: '14px', color: '#757373' }}>
            Couldn't load sub trades.{' '}
            <Anchor component="button" type="button" fz="14px" onClick={() => dispatch(loadTradeSubTrades(row.id) as any)}>
              Retry
            </Anchor>
          </Text>
        ) : (
          <Text style={{ fontFamily: 'Plus Jakarta Sans', fontSize: '14px', color: '#757373' }}>
            No sub trades
          </Text>
        )}
      </Stack>
    )
  }

  return (
    <Stack w="100%" className="sectionDetailTradeBody noHeader">
      <Stack w='100%' className='sectionRemoveGap sectionBody'>
        <Stack w='100%' className='sectionDetailTradeBody' style={{ padding: '10px' }}>
          <MainTable
            tableName={'AA'}
            columnDefs={columnDefs}
            withPagination={false}
            data={refinedSubTrades ?? []}
            setRow={() => { }}
            enableTopToolbar={false}
            headerBackgroundColor='#F0EEEE'
            enableColumnOrdering={false}
            enableColumnResizing={false}
            enableGrouping={false}
            enableColumnFilters={false}
            enableColumnActions={false}
            enableFilters={false}
            enableSorting={false}
            enableFullScreenToggle={false}
            enableDensityToggle={false}
            enableHiding={false}
            border={true}
            fromTradeEntry={false}
          />
        </Stack>
      </Stack>
    </Stack>
  );
};

export default TradeEntryPanel;
